// src/features/error-definitions/components/ErrorDefinitionList.tsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  TextField,
  MenuItem,
  Alert,
  TablePagination,
  Tooltip,
} from '@mui/material';
import {
  WifiOff as NetworkIcon,
  Build as MechanicalIcon,
  CreditCard as EncoderIcon,
  CreditCard as CardIcon,
  MeetingRoom as RoomIcon,
  QrCode as QrIcon,
  Apartment as PmsIcon,
  Lock as LocksIcon,
  Computer as SystemIcon,
  Payments as PaymentsIcon,
  Badge as IdentificationIcon,
  Rule as RuleIcon,
  HelpOutline as UnknownIcon,
} from '@mui/icons-material';
import {
  useTranslate,
  useDataProvider,
  useNotify,
  usePermissions,
  useLocale,
  ListContextProvider,
  FunctionField,
} from 'react-admin';
import type {
  ErrorDefinition,
  ErrorSeverity,
  ErrorCategory,
  ErrorDefinitionListResponse,
} from '@/types/errorDefinitions';
import {
  getSeverityColor,
  getSeverityBgColor,
} from '@/types/errorDefinitions';
import { ErrorDefinitionDialog } from './ErrorDefinitionDialog';
import ErrorDefinitionActions from './ErrorDefinitionActions';
import { CustomDataGrid } from '@/components/common';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FilterPopoverProvider } from '@/components/common/RaFilterPopover';
import { SelfFilterPopover } from '@/components/common/SelfFilterPopover';

// Category icon components mapping
const CATEGORY_ICON_COMPONENTS: Record<ErrorCategory, React.ElementType> = {
  Encoder: EncoderIcon,
  Mechanical: MechanicalIcon,
  Card: CardIcon,
  Room: RoomIcon,
  Network: NetworkIcon,
  System: SystemIcon,
  PMS: PmsIcon,
  Locks: LocksIcon,
  QR: QrIcon,
  Payments: PaymentsIcon,
  Identification: IdentificationIcon,
};

const SEVERITY_OPTIONS: ErrorSeverity[] = ['Critical', 'Warning', 'Info'];
const CATEGORY_OPTIONS: ErrorCategory[] = ['Encoder', 'Mechanical', 'Card', 'Room', 'Network', 'System', 'PMS', 'Locks', 'QR', 'Payments', 'Identification'];

interface ErrorDefinitionListProps {
  /** When embedded in ErrorMonitor, filters feed the parent's shared popover and
   *  no own header card is rendered. Standalone wraps its own header + provider. */
  embedded?: boolean;
}

/**
 * Error Definitions management list
 * SuperAdmin only - CRUD operations for error definitions
 *
 * Can be embedded inside ErrorMonitor (via SubMenu) or rendered standalone.
 * Filters live in this component's own state and are surfaced through the shared
 * SectionHeader popover (SelfFilterPopover).
 */
export const ErrorDefinitionList: React.FC<ErrorDefinitionListProps> = ({
  embedded = false,
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { permissions } = usePermissions();
  const locale = useLocale();

  // State
  const [data, setData] = useState<ErrorDefinitionListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState<ErrorSeverity | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<ErrorCategory | ''>('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDefinition, setEditingDefinition] = useState<ErrorDefinition | null>(null);

  // Check permissions
  const isSuperAdmin = permissions === 'SuperAdmin' || permissions === 'Admin';

  // Listen for create event from parent
  useEffect(() => {
    const handleCreate = () => {
      setEditingDefinition(null);
      setDialogOpen(true);
    };
    window.addEventListener('error-definition-create', handleCreate);
    return () => window.removeEventListener('error-definition-create', handleCreate);
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await dataProvider.getErrorDefinitions({
        page: page + 1,
        pageSize: rowsPerPage,
        search: searchTerm || undefined,
        severity: severityFilter || undefined,
        category: categoryFilter || undefined,
      });
      setData(response.data);
    } catch (err: any) {
      console.error('Error fetching error definitions:', err);
      setError(err.message || translate('resources.errorDefinitions.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [dataProvider, page, rowsPerPage, searchTerm, severityFilter, categoryFilter, translate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handlers
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEdit = (definition: ErrorDefinition) => {
    setEditingDefinition(definition);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingDefinition(null);
  };

  const handleDialogSave = () => {
    setDialogOpen(false);
    setEditingDefinition(null);
    fetchData();
  };

  // Permission check
  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {translate('resources.errorDefinitions.errors.noPermission')}
        </Alert>
      </Box>
    );
  }

  const definitions = data?.data || [];
  const totalCount = data?.pagination?.totalCount || 0;

  // Create list context for CustomDataGrid
  const listContext = {
    data: definitions,
    total: totalCount,
    isLoading: loading,
    page: page + 1,
    perPage: rowsPerPage,
    setPage: (p: number) => setPage(p - 1),
    setPerPage: setRowsPerPage,
    sort: { field: 'errorCode', order: 'ASC' as const },
    filterValues: {},
    resource: 'error-definitions',
    selectedIds: [],
    onSelect: () => {},
    onToggleItem: () => {},
    onUnselectItems: () => {},
    refetch: fetchData,
  };

  const activeFilterCount = [searchTerm, severityFilter, categoryFilter].filter(Boolean).length;

  // Filters surfaced through the shared SectionHeader popover (own state).
  const filterFields = (
    <SelfFilterPopover count={activeFilterCount} onRefresh={fetchData} refreshing={loading}>
      <TextField
        size="small"
        label={translate('resources.errorDefinitions.filters.search')}
        value={searchTerm}
        onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 180 }}
      />
      <TextField
        select
        size="small"
        label={translate('resources.errorDefinitions.filters.severity')}
        value={severityFilter}
        onChange={(e) => { setSeverityFilter(e.target.value as ErrorSeverity | ''); setPage(0); }}
        InputLabelProps={{ shrink: true }}
        SelectProps={{ displayEmpty: true }}
        sx={{ minWidth: 130 }}
      >
        <MenuItem value="">
          {translate('resources.errorDefinitions.filters.all')}
        </MenuItem>
        {SEVERITY_OPTIONS.map((severity) => (
          <MenuItem key={severity} value={severity}>
            {translate(`resources.errorDefinitions.severity.${severity.toLowerCase()}`)}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label={translate('resources.errorDefinitions.filters.category')}
        value={categoryFilter}
        onChange={(e) => { setCategoryFilter(e.target.value as ErrorCategory | ''); setPage(0); }}
        InputLabelProps={{ shrink: true }}
        SelectProps={{ displayEmpty: true }}
        sx={{ minWidth: 130 }}
      >
        <MenuItem value="">
          {translate('resources.errorDefinitions.filters.all')}
        </MenuItem>
        {CATEGORY_OPTIONS.map((category) => (
          <MenuItem key={category} value={category}>
            {translate(`resources.errorDefinitions.category.${category.toLowerCase()}`)}
          </MenuItem>
        ))}
      </TextField>
    </SelfFilterPopover>
  );

  const content = (
    <Box>
      {filterFields}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {/* Table with CustomDataGrid */}
      <Paper elevation={0} sx={{ border: 'none' }}>
        <ListContextProvider value={listContext as any}>
          <CustomDataGrid bulkActionButtons={false}>
            {/* Error Code */}
            <FunctionField
              label={translate('resources.errorDefinitions.fields.errorCode')}
              render={(record: ErrorDefinition) => (
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                  {record.errorCode}
                </Typography>
              )}
            />

            {/* Severity */}
            <FunctionField
              label={translate('resources.errorDefinitions.fields.severity')}
              render={(record: ErrorDefinition) => {
                const severityColor = getSeverityColor(record.severity);
                const severityBgColor = getSeverityBgColor(record.severity);
                return (
                  <Chip
                    label={translate(`resources.errorDefinitions.severity.${record.severity.toLowerCase()}`)}
                    size="small"
                    sx={{
                      backgroundColor: severityBgColor,
                      color: severityColor,
                      fontWeight: 600,
                    }}
                  />
                );
              }}
            />

            {/* Category */}
            <FunctionField
              label={translate('resources.errorDefinitions.fields.category')}
              render={(record: ErrorDefinition) => {
                const IconComponent = CATEGORY_ICON_COMPONENTS[record.category] || UnknownIcon;
                return (
                  <Chip
                    icon={<IconComponent sx={{ fontSize: '16px !important' }} />}
                    label={translate(`resources.errorDefinitions.category.${record.category.toLowerCase()}`)}
                    size="small"
                    variant="outlined"
                  />
                );
              }}
            />

            {/* Title / Description / Recommendation */}
            <FunctionField
              label={translate('resources.errorDefinitions.fields.title')}
              render={(record: ErrorDefinition) => {
                // Find translation for current locale, fallback to English, then default
                const currentTranslation = record.translations?.find(
                  (t) => t.language === locale || t.languageCode === locale
                );
                const enTranslation = record.translations?.find(
                  (t) => t.language === 'en' || t.languageCode === 'en'
                );
                const translation = currentTranslation || enTranslation;
                const title = translation?.title || record.defaultTitle || '-';
                const description = translation?.description || record.defaultDescription;
                const recommendation = translation?.recommendation || translation?.suggestedAction;

                return (
                  <Box sx={{ maxWidth: 350 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {title}
                    </Typography>
                    {description && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                        {description}
                      </Typography>
                    )}
                    {recommendation && (
                      <Typography variant="caption" color="info.main" display="block" sx={{ mt: 0.25, fontStyle: 'italic' }}>
                        {recommendation}
                      </Typography>
                    )}
                  </Box>
                );
              }}
            />

            {/* Translations */}
            <FunctionField
              label={translate('resources.errorDefinitions.fields.translations')}
              render={(record: ErrorDefinition) => {
                const languages = (record.translations || [])
                  .map((t) => (t.language || t.languageCode || '').toUpperCase())
                  .filter(Boolean);

                if (languages.length === 0) {
                  return (
                    <Typography variant="caption" color="text.secondary">-</Typography>
                  );
                }

                return (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {languages.map((lang) => (
                      <Chip
                        key={lang}
                        label={lang}
                        size="small"
                        variant="outlined"
                        sx={{
                          minWidth: 32,
                          '& .MuiChip-label': { px: 0.75 }
                        }}
                      />
                    ))}
                  </Box>
                );
              }}
            />

            {/* Conditions */}
            <FunctionField
              label={translate('resources.errorDefinitions.fields.conditions')}
              render={(record: ErrorDefinition) => {
                const conditions = record.conditions || [];
                if (conditions.length === 0) {
                  return (
                    <Typography variant="caption" color="text.secondary">-</Typography>
                  );
                }

                const summary = conditions
                  .map((c) => `${c.metadataKey} = ${c.metadataValue}`)
                  .join('\n');

                return (
                  <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{summary}</span>}>
                    <Chip
                      icon={<RuleIcon sx={{ fontSize: '16px !important' }} />}
                      label={translate('resources.errorDefinitions.conditionCount', {
                        smart_count: conditions.length,
                      })}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Tooltip>
                );
              }}
            />

            {/* Actions */}
            <FunctionField
              label={translate('resources.errorDefinitions.fields.actions')}
              render={(record: ErrorDefinition) => (
                <ErrorDefinitionActions
                  record={record}
                  onEdit={handleEdit}
                  onDeleted={fetchData}
                />
              )}
            />
          </CustomDataGrid>
        </ListContextProvider>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={translate('ra.navigation.page_rows_per_page')}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <ErrorDefinitionDialog
        open={dialogOpen}
        definition={editingDefinition}
        onClose={handleDialogClose}
        onSave={handleDialogSave}
      />
    </Box>
  );

  // Embedded in ErrorMonitor: the parent renders the SectionHeader + provider.
  if (embedded) return content;

  // Standalone route: wrap with its own header card + filter provider.
  return (
    <FilterPopoverProvider>
      <Box sx={{ p: { xs: 1, sm: 1 } }}>
        <SectionHeader manualScreen="error-definitions"
          useRaFilters
          showCreate
          onCreateClick={() => window.dispatchEvent(new CustomEvent('error-definition-create'))}
        />
        {content}
      </Box>
    </FilterPopoverProvider>
  );
};

export default ErrorDefinitionList;
